import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Toolbar,
    Alert,
    Tooltip,
} from '@mui/material';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import type { GridColDef } from '@mui/x-data-grid';
import FieldNames from '../../../defaults/FieldNames.json';
import { useSelector } from 'react-redux';
import FundingService from '../../../services/FundingService';
import ColorConstants from '../../../defaults/ColorConstants';
import { currencyFormat } from '../../../utils/FormatHelper';
import {
    mergeFundingRowUpdate,
    recalcProportionalFundingAcres,
    buildZeroedRemainingFundingRows,
    clampNonNegativeCostFields,
    totalFundingAmount,
    computeRowLevelFundingTotals,
} from '../../../utils/FundingHelper';
import SubstitutionManageFunding from '../SubstitutionManageFunding/SubstitutionManageFunding.tsx';


interface SubstituteNewActionProps {
    open: boolean;
    onClose: () => void;
    onSave: (data: {
        selectedIds: any;
        initFunding: any;
        updatedFunding: any;
        remainingFundingRows: any;
    }) => void;
    joinId: string;
    joinFeature: string;
}

const FF = FieldNames.fundingFields;

export default function SubstituteNewAction({
    open,
    onClose,
    onSave,
    joinId,
    joinFeature,
}: SubstituteNewActionProps) {
    const [editedFundingRows, setEditedFundingRows] = useState<any[]>([]);
    const [initFundingRows, setInitFundingRows] = useState<any[]>([]);
    const [selectedActionCalculatedArea, setSelectedActionCalculatedArea] = useState<number>(0);

    // Rows representing the funding that stays behind on the original
    // feature. Seeded at $0 for every cost field - the user must manually
    // move amounts here (out of the "moved" grid above) so the grand total
    // across both grids still equals the original funding total. Nothing is
    // redistributed/subtracted automatically.
    const [remainingFundingRows, setRemainingFundingRows] = useState<any[]>([]);
    const feature = useSelector((state: any) => state.details.feature);
    const isPostFire = feature?.attributes?.[FieldNames.featureFields.entityType] === 'PostFire';
    const calculatedArea = feature?.attributes?.[FieldNames.featureFields.calculatedArea] || 0;

    const [totalsMatch, setTotalsMatch] = useState<boolean>(false);



    const [fundingRows, setFundingRows] = useState<any[]>([]);

    const [selectedFundingIds, setSelectedFundingIds] = useState<any>({
        ids: new Set(),
        type: 'include',
    });

    useEffect(() => {

        setEditedFundingRows(fundingRows.map((row: any) => ({
                        ...row,
                        rowId: row[FF.id] || row[FF.objectID],
                        [FF.joinID]: null,
                    })))
        }, [fundingRows]
    );

    const handleSave = useCallback(() => {
        const selectedIds = Array.from(selectedFundingIds.ids).map(Number);

        // // Only rows the user has selected actually move to the new substitute feature.
        const movedFundingRows = editedFundingRows.filter((row) =>
            selectedIds.includes(Number(row[FF.id] ?? row[FF.objectID]))
        );


        const finalRemainingFundingRows = recalcProportionalFundingAcres(
            remainingFundingRows,
            calculatedArea,
            isPostFire
        );

        onSave({
            selectedIds,
            initFunding: initFundingRows,
            updatedFunding: movedFundingRows,
            remainingFundingRows: finalRemainingFundingRows,
        });
        onClose();
    }, [
        selectedFundingIds,
        editedFundingRows,
        initFundingRows,
        remainingFundingRows,
        calculatedArea,
        isPostFire,
        onSave,
        onClose,
    ]);


    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth='xl'
            fullWidth
            slotProps={{
                paper: {
                    sx: {
                        height: '80vh',
                    },
                },
            }}
        >
            <DialogTitle
                sx={{
                    backgroundColor: ColorConstants.BlueAlien,
                    color: ColorConstants.WhiteAlien,
                }}
            >
                Substitution - New Action
            </DialogTitle>
            <DialogContent sx={{paddingBottom: 0}} dividers>
                <Typography variant='body2' sx={{ mb: 2, fontStyle: 'italic' }}>
                    Action will be copied 'as-is' with the existing name + '_SUBSTITUTE'. You will
                    be redirected to the new action after clicking 'SAVE'; Geometry and attribute
                    data can be updated at that time.
                </Typography>

                <SubstitutionManageFunding
                    joinId={joinId}
                    joinFeature={joinFeature}
                    fundingRows={fundingRows}
                    setFundingRows={setFundingRows}
                    initFundingRows={initFundingRows}
                    setInitFundingRows={setInitFundingRows}
                    remainingFundingRows={remainingFundingRows}
                    setRemainingFundingRows={setRemainingFundingRows}
                    selectedFundingIds={selectedFundingIds}
                    setSelectedFundingIds={setSelectedFundingIds}
                    selectedActionCalculatedArea={calculatedArea}
                    open={open}
                    setTotalsMatch={setTotalsMatch}
                ></SubstitutionManageFunding>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant='contained' color='primary'>
                    CANCEL
                </Button>
                <Tooltip
                    title={
                        !totalsMatch
                            ? 'Funding totals must match before saving'
                            : selectedFundingIds.ids.size === 0
                              ? 'At least one funding row must be selected'
                              : ''
                    }
                >
                    <span>
                        <Button
                            onClick={handleSave}
                            variant='contained'
                            color='primary'
                            disabled={selectedFundingIds.ids.size === 0 || !totalsMatch}
                        >
                            SAVE
                        </Button>
                    </span>
                </Tooltip>
            </DialogActions>
        </Dialog>
    );
}